import React, { useEffect, useReducer } from "react";
import { useForm } from "react-final-form";
import FormBottomRow from "~/components/form-bottom-row";
import {
  ChargeDatePicker,
  ChargeEditNotes,
  ChargeEeCodePicker,
  ChargeEmployeePicker,
  ChargeInvoiceNotes,
  ChargeProjectField,
  ChargeQuantityField,
  ChargeRateAmountField,
  ChargeRateField,
  ChargeRateUnitField,
  CustomRateLabelField,
} from "./EditChargeFields";
import {
  ChargeBillableToggle,
  ChargeOverrideRateToggle,
  ChargeTaxableToggle,
} from "./EditChargeToggles";
import Spinner from "~/components/spinner";
import styled from "@emotion/styled";
import { ChargeFieldState, ChargeFormFieldsProps } from "./types";
import { ChargeType } from "~/gql/types";
import { deriveFieldStates } from "./functions/deriveFieldStates";
import { useChargeFormGraphqlApi } from "./chargeFormProviders/ChargeFormGraphqlApi";
import { initialState, reducer } from "./stateMachine/stateMachine";

const FormRow = styled("div")`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: center;
`;

export const ChargeFormFields: React.FC<ChargeFormFieldsProps> = ({
  charge,
  error,
  values,
  onCancel,
}) => {
  const formApi = useForm();
  const {
    getEffectiveExpenseRateSheet,
    getEffectiveTaskRateSheet,
    getEmployeeRate,
    getPerDiemRate,
  } = useChargeFormGraphqlApi();
  const [state, dispatch] = useReducer(
    reducer,
    {
      date: values.date,
      project: values.project,
      useCustomRate: values.useCustomRate,
      employee: values.employee,
      rate: values.rate,
      rateSheetName: values.rateSheetName,
    },
    initialState
  );

  // form value changes → actions. these fire on mount too, while the machine
  // is in loading-initial, which ignores them.
  useEffect(() => {
    dispatch({
      tag: "rate-sheet-dependency-changed",
      formFields: {
        date: values.date,
        project: values.project,
        useCustomRate: values.useCustomRate,
      },
    });
  }, [values.date, values.project, values.useCustomRate]);

  useEffect(() => {
    dispatch({ tag: "rate-changed", name: "rate", value: values.rate });
  }, [values.rate]);

  useEffect(() => {
    dispatch({
      tag: "employee-changed",
      name: "employee",
      value: values.employee,
    });
  }, [values.employee]);

  // machine state → queries
  useEffect(() => {
    async function runQuery() {
      if (state.tag === "loading-initial" || state.tag === "reloading") {
        const { date, project } = state.formFields;

        try {
          const asOf = date!.format("YYYY-MM-DD");
          const rateSheet =
            charge.type === ChargeType.Expense
              ? await getEffectiveExpenseRateSheet(asOf, project!.number)
              : await getEffectiveTaskRateSheet(asOf, project!.number);
          dispatch({ tag: "form-data-loaded", rateSheet });
        } catch (e) {
          dispatch({ tag: "form-data-failed", error: `${e}` });
        }
      }

      if (state.tag === "loading-employee-rate-info") {
        const { employee, date } = state.formFields;

        try {
          const employeeRate = await getEmployeeRate(
            employee!.userPrincipalName,
            date!.format("YYYY-MM-DD")
          );
          dispatch({ tag: "employee-rate-loaded", employeeRate });
        } catch (e) {
          dispatch({ tag: "extended-rate-failed", error: `${e}` });
        }
      }

      if (state.tag === "loading-per-diem-rate-info") {
        const { project, date } = state.formFields;

        try {
          const perDiemRate = await getPerDiemRate(
            date!.format("YYYY-MM-DD"),
            project!.location!.state,
            project!.location!.county
          );
          dispatch({ tag: "per-diem-rate-loaded", perDiemRate });
        } catch (e) {
          dispatch({ tag: "extended-rate-failed", error: `${e}` });
        }
      }
    }
    void runQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tag]);

  // emitted field changes → form
  const pendingFieldChanges =
    "outboundFieldMutations" in state
      ? state.outboundFieldMutations
      : undefined;

  useEffect(() => {
    if (!pendingFieldChanges) return;

    formApi.batch(() => {
      Object.entries(pendingFieldChanges).forEach(([field, value]) =>
        formApi.change(field, value)
      );
    });
    dispatch({ tag: "outbound-fields-written" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const rateSuggestions =
    "rateSheet" in state ? state.rateSheet?.rates ?? null : null;
  const rateAmountValidationMessage =
    "validationMessages" in state
      ? state.validationMessages?.rateAmount ?? null
      : null;

  const fieldStates = deriveFieldStates(
    {
      useCustomRate: values.useCustomRate,
      date: values.date,
      project: values.project,
      rateSheetName: values.rateSheetName,
    },
    charge
  );

  // the inputs that dispatch actions are locked until the machine is ready
  const machineBusy = state.tag !== "ready";
  const lockedWhileBusy = (fieldState: ChargeFieldState): ChargeFieldState => ({
    ...fieldState,
    disabled: fieldState.disabled || machineBusy,
  });

  return (
    <div>
      <FormRow>
        {fieldStates.date.visible && (
          <ChargeDatePicker {...lockedWhileBusy(fieldStates.date)} />
        )}
        {fieldStates.employee.visible && (
          <ChargeEmployeePicker {...lockedWhileBusy(fieldStates.employee)} />
        )}
      </FormRow>

      <FormRow>
        <ChargeBillableToggle {...fieldStates.billable} />
        {fieldStates.eeCode.visible && (
          <ChargeEeCodePicker {...fieldStates.eeCode} />
        )}
      </FormRow>

      <FormRow>
        {fieldStates.taxable.visible && (
          <ChargeTaxableToggle {...fieldStates.taxable} />
        )}
        <ChargeQuantityField {...fieldStates.quantity} />
      </FormRow>

      <FormRow>
        {fieldStates.project.visible && (
          <ChargeProjectField
            {...lockedWhileBusy(fieldStates.project)}
            date={values.date}
          />
        )}
        <ChargeRateAmountField
          {...fieldStates.rateAmount}
          rateAmountValidationMessage={rateAmountValidationMessage}
        />
      </FormRow>

      <FormRow>
        <ChargeOverrideRateToggle
          {...lockedWhileBusy(fieldStates.useCustomRate)}
        />
        <ChargeRateUnitField {...fieldStates.unit} />
        <ChargeInvoiceNotes {...fieldStates.notes} />
      </FormRow>

      <FormRow>
        {fieldStates.customRateLabel.visible && (
          <CustomRateLabelField customRateLabel={fieldStates.customRateLabel} />
        )}
        {fieldStates.rate.visible && (
          <ChargeRateField
            rate={lockedWhileBusy(fieldStates.rate)}
            rateSuggestions={rateSuggestions}
            type={values.type}
          />
        )}
        <div></div>
        {fieldStates.editNotes.visible && (
          <ChargeEditNotes {...fieldStates.editNotes} />
        )}
      </FormRow>

      <FormBottomRow
        errorMessage={error}
        buttonText="Save"
        onCancel={() => {
          onCancel();
        }}
      />

      {/* replace with loading states */}
      <Spinner open={false} />
    </div>
  );
};
